/**
 * "Right now": one row per robot, under the attention list (twin slice 1,
 * plan W5 step 13; ideation §6, "grouped by floor above 5 robots").
 *
 * Every row says only what W3's presence lets it say ("solid means
 * reporting now"), read straight off `RobotPresence`:
 *
 *   live      its floor, what it is doing, its battery.
 *   quiet     the same, dimmed, and no battery: the reading is 10-30 s
 *             old, so a percentage would pass an old figure off as now.
 *   checking  its floor, "Checking", dimmed: seen once, not yet trusted.
 *   settling  its floor and the settling words (`SETTLING_TEXT`, the
 *             drawer's and the resolver's own), dimmed.
 *   riding    "In SL1": its own pose still names the floor it boarded on,
 *             so no floor is given. Presence holds a robot riding only
 *             while its own receipts keep arriving (contract §12 A6): a
 *             rider is never more than 30 s unheard, and its `quiet` and
 *             `checking` flags say whether it is reporting now. Dimmed
 *             while either is set; solid otherwise.
 *   lost      dimmed, and never under a floor heading: a last-known floor
 *             is not where it is. "No signal" only where the robot itself
 *             is unheard (`UNHEARD_CAUSES`, shared with the drawer's
 *             line), never for the (0, 0) sentinel, which a robot that is
 *             still reporting can send. Its last real pose, with when:
 *             inside a car it names the CAR ("Last seen in SL1 at
 *             14:02"), because a rider's pose names the floor it boarded
 *             on; anywhere else the floor.
 *   unknown   the robot copy's "Lost its place on the map", no floor.
 *
 * While the page's feed is clamped (W3's FeedHealth: reconnecting, woken,
 * silent) no row is reporting now: every row is dimmed and no battery is
 * shown, as the drawer's battery reads "—" (`robot-lines`).
 *
 * Simulated robots are shown only to an audience that may see them
 * (`canSeeSimulated`), tagged.
 *
 * COPY STATUS: pending Akash's copy pass 1 (plan gate B); listed as COPY
 * FOR REVIEW.
 */
import { canSeeSimulated, type RoleLike } from '../../auth/roles';
import {
  SETTLING_TEXT,
  UNHEARD_CAUSES,
  type RobotPresence,
} from '../../screens/Map/robot-presence';
import { robotCopyRow } from './robot-copy';
import { dayClockText, levelWords, type SiteTimeZone } from './words';

/** Above this many robots the rows are grouped by floor. */
export const RIGHT_NOW_GROUP_ABOVE = 5;

export interface RightNowRobot {
  fleet: string;
  name: string;
  /** `RobotStateView.status`. */
  status: string;
  /** `RobotStateView.battery` (percent); undefined or null when not reported. */
  batteryPct?: number | null;
  simulated: boolean;
  /** W3's presence for this robot: everything a row says about whether
   *  and where it is reporting. */
  presence: Pick<RobotPresence, 'kind' | 'cause' | 'pose' | 'liftName' | 'quiet' | 'checking'>;
}

export interface RightNowRow {
  key: string;
  name: string;
  /** The floor it is on, as far as presence trusts it ("Level 9"), or
   *  where it was last recorded for a lost robot; null when neither. */
  where: string | null;
  /** What it is doing, or what is known about it. Empty when the status
   *  has no words. */
  state: string;
  /** "58%", only for a live reading. */
  battery: string | null;
  /** Not reporting now: drawn dimmed. */
  dim: boolean;
  simulated: boolean;
}

export interface RightNowGroup {
  /** The floor's map name, or null for "floor not known". */
  level: string | null;
  /** "Level 9", or "Floor not known"; null when the rows are not grouped. */
  heading: string | null;
  rows: RightNowRow[];
}

/** What a status says, after the robot's name. Statuses not here say
 *  nothing: an internal token is never shown. */
const STATUS_WORDS: Readonly<Record<string, string>> = {
  idle: 'Idle',
  charging: 'Charging',
  active: 'Working',
  error: robotCopyRow('robot_error').headline({ robot: '', arg: null, level: null }),
};

export const NO_SIGNAL = 'No signal';
export const FLOOR_NOT_KNOWN = 'Floor not known';
export const CHECKING = 'Checking';
/** A lost robot that may still be reporting (the (0, 0) sentinel) with no
 *  real pose to show: the drawer's own words (`robot-lines`). */
export const NOT_REPORTING_A_POSITION = 'Not reporting a position';

function battery(pct: number | null | undefined): string | null {
  return typeof pct === 'number' && Number.isFinite(pct) && pct >= 0 && pct <= 100
    ? `${Math.round(pct)}%`
    : null;
}

/** Where a lost robot was last recorded, and what may be said of it. Its
 *  time names the day when it is not today ("yesterday 23:58"), as the
 *  drawer's does (`formatClock`; copy deck v2, A5): a bare "23:58" from
 *  last night read as this morning's. */
function lostRow(
  p: RightNowRobot['presence'],
  clock: SiteTimeZone,
  wallNowMs: number,
): Pick<RightNowRow, 'where' | 'state'> {
  const unheard = UNHEARD_CAUSES.has(p.cause);
  const ghost = p.pose && p.pose.source !== 'live' ? p.pose : null;
  if (!ghost) return { where: null, state: unheard ? NO_SIGNAL : NOT_REPORTING_A_POSITION };
  const at =
    typeof ghost.atMs === 'number' && Number.isFinite(ghost.atMs)
      ? ` at ${dayClockText(ghost.atMs, clock, wallNowMs)}`
      : '';
  // Presence names the car only when the ghost stands inside one.
  const where = p.liftName
    ? `${unheard ? 'Last seen' : 'Last recorded'} in ${p.liftName}${at}`
    : `Last recorded on ${levelWords(ghost.map)}${at}`;
  return { where, state: unheard ? NO_SIGNAL : '' };
}

function rowFor(
  r: RightNowRobot,
  clock: SiteTimeZone,
  clamped: boolean,
  wallNowMs: number,
): { row: RightNowRow; level: string | null } {
  const p = r.presence;
  const status = STATUS_WORDS[r.status] ?? '';
  const base = { key: `${r.fleet}/${r.name}`, name: r.name, simulated: r.simulated };
  const here = p.pose?.source === 'live' ? p.pose.map : null;
  switch (p.kind) {
    case 'live':
      return {
        row: {
          ...base,
          where: levelWords(here),
          state: status,
          battery: clamped ? null : battery(r.batteryPct),
          dim: clamped,
        },
        level: here,
      };
    case 'quiet':
      return {
        row: { ...base, where: levelWords(here), state: status, battery: null, dim: true },
        level: here,
      };
    case 'checking':
      return {
        row: { ...base, where: levelWords(here), state: CHECKING, battery: null, dim: true },
        level: here,
      };
    case 'settling':
      return {
        row: {
          ...base,
          where: levelWords(here),
          state: SETTLING_TEXT,
          battery: null,
          dim: true,
        },
        level: here,
      };
    case 'riding':
      return {
        row: {
          ...base,
          where: null,
          state: p.liftName ? `In ${p.liftName}` : 'In a lift',
          battery: null,
          dim: clamped || p.quiet || p.checking,
        },
        level: null,
      };
    case 'lost':
      return {
        row: { ...base, ...lostRow(p, clock, wallNowMs), battery: null, dim: true },
        level: null,
      };
    case 'unknown':
      return {
        row: {
          ...base,
          where: null,
          state: robotCopyRow('not_localized').headline({ robot: r.name, arg: null, level: null }),
          battery: null,
          dim: true,
        },
        level: null,
      };
  }
}

/** `L4` < `L4M` < `L6` < `L17`; anything else after, by name. */
function floorOrder(a: string, b: string): number {
  const m = (s: string) => /^L0*(\d+)(M?)$/i.exec(s);
  const ma = m(a);
  const mb = m(b);
  if (ma && mb) return Number(ma[1]) - Number(mb[1]) || ma[2]!.localeCompare(mb[2]!);
  if (ma) return -1;
  if (mb) return 1;
  return a.localeCompare(b);
}

/** One row per robot this viewer may see; grouped by floor above five.
 *  `feed.clamped`: nothing on screen is reporting now (W3's FeedHealth).
 *  `wallNowMs` is this device's wall clock, read only to name the day of
 *  a time that is not today; never subtracted from a stamp. */
export function rightNow(
  robots: readonly RightNowRobot[],
  roles: readonly RoleLike[],
  clock: SiteTimeZone,
  feed: { readonly clamped: boolean } | null | undefined,
  wallNowMs: number,
): RightNowGroup[] {
  const clamped = feed?.clamped === true;
  const shown = robots.filter((r) => !r.simulated || canSeeSimulated(roles));
  const rows = shown
    .map((r) => rowFor(r, clock, clamped, wallNowMs))
    .sort((a, b) => a.row.name.localeCompare(b.row.name) || a.row.key.localeCompare(b.row.key));
  if (rows.length === 0) return [];
  if (rows.length <= RIGHT_NOW_GROUP_ABOVE)
    return [{ level: null, heading: null, rows: rows.map((x) => x.row) }];
  const byLevel = new Map<string | null, RightNowRow[]>();
  for (const { row, level } of rows) byLevel.set(level, [...(byLevel.get(level) ?? []), row]);
  return [...byLevel.entries()]
    .sort(([a], [b]) => (a === null ? 1 : b === null ? -1 : floorOrder(a, b)))
    .map(([level, list]) => ({
      level,
      heading: level === null ? FLOOR_NOT_KNOWN : levelWords(level),
      rows: list,
    }));
}
